(() => {
    /*
     * Shopify Inbox renders an autonomous <shopify-chat> web component with no public
     * "open" API, so we drive its own launcher: click the button.activator (which toggles
     * the chat) and mirror the host's `open` attribute back onto our triggers. These are
     * Inbox internals — renamed wholesale in the 2026-07 rewrite, restructured again in
     * 2026-08 — so every access is guarded, and the shadow-piercing deepQuery keeps
     * working whether the activator sits in the host's own root or a nested one. A miss
     * degrades to "native launcher stays visible" rather than a dead trigger. The launcher
     * is hidden by injecting a stylesheet into its own shadow tree: Inbox sets .activator
     * display with an internal rule that a document-level ::part() override cannot beat
     * across the forwarded-part boundary, but a same-tree !important rule can.
     *
     * Since 2026-08 the bare <shopify-chat> tag is server-rendered in the page HTML and
     * upgraded later by Shopify's async module script, so the host we resolve usually has
     * no shadow root yet — binding waits on customElements.whenDefined (the component
     * attaches its shadow root in its constructor, so post-upgrade it always exists).
     */
    const HOST_TAG = 'shopify-chat';
    const ACTIVATOR_SELECTOR = 'button.activator';
    const TRIGGER_SELECTOR = '[data-inbox-chat-trigger]';
    const OPEN_ATTR = 'open';
    const HIDE_MARKER = 'data-inbox-hide';
    const HIDE_CSS = '.activator{display:none!important}';
    const deepQuery = (root, selector) => {
        if (!root)
            return null;
        const direct = root.querySelector(selector);
        if (direct)
            return direct;
        for (const el of root.querySelectorAll('*')) {
            if (el.shadowRoot) {
                const found = deepQuery(el.shadowRoot, selector);
                if (found)
                    return found;
            }
        }
        return null;
    };
    let host = null;
    let pendingOpen = false;
    const activator = () => (host ? deepQuery(host.shadowRoot, ACTIVATOR_SELECTOR) : null);
    const isOpen = () => !!(host && host.hasAttribute(OPEN_ATTR));
    const flushPending = () => {
        if (!pendingOpen)
            return;
        const button = activator();
        if (!button)
            return;
        pendingOpen = false;
        button.click();
    };
    const hideLauncher = () => {
        const button = activator();
        if (!button)
            return;
        const root = button.getRootNode();
        if (!(root instanceof ShadowRoot) || root.querySelector(`style[${HIDE_MARKER}]`))
            return;
        const style = document.createElement('style');
        style.setAttribute(HIDE_MARKER, '');
        style.textContent = HIDE_CSS;
        root.appendChild(style);
    };
    const syncTriggers = () => {
        const expanded = isOpen();
        document.querySelectorAll(TRIGGER_SELECTOR).forEach((el) => {
            el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            el.classList.toggle('is-active', expanded);
        });
    };
    const open = () => {
        if (isOpen())
            return;
        const button = activator();
        if (button) {
            button.click();
            return;
        }
        pendingOpen = true;
    };
    const close = () => {
        pendingOpen = false;
        if (isOpen())
            activator()?.click();
    };
    const ready = (el) => {
        if (host)
            return;
        host = el;
        new MutationObserver(syncTriggers).observe(host, {
            attributes: true,
            attributeFilter: [OPEN_ATTR],
        });
        const bind = () => {
            hideLauncher();
            syncTriggers();
            flushPending();
            if (!el.shadowRoot)
                return;
            new MutationObserver(() => {
                hideLauncher();
                syncTriggers();
                flushPending();
            }).observe(el.shadowRoot, { childList: true, subtree: true });
        };
        if (el.shadowRoot)
            bind();
        else
            customElements.whenDefined(HOST_TAG).then(bind);
    };
    const watch = () => {
        const existing = document.querySelector(HOST_TAG);
        if (existing) {
            ready(existing);
            return;
        }
        const observer = new MutationObserver(() => {
            const el = document.querySelector(HOST_TAG);
            if (el) {
                observer.disconnect();
                ready(el);
            }
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
    };
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!target || !target.closest)
            return;
        const trigger = target.closest(TRIGGER_SELECTOR);
        if (!trigger)
            return;
        event.preventDefault();
        // Inbox closes the chat on any click outside it, and takes the press on our
        // trigger for one when the chat opened inside that press, so the chat opens
        // or closes once the press is over, as it stood when the press came.
        const wasOpen = isOpen();
        setTimeout(wasOpen ? close : open);
    });
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', watch);
    }
    else {
        watch();
    }
})();
